import type { Bewerber } from '../../types/bewerber';
import { useBewerberActions, BewerberActionModals } from './actions';

interface StatusActionButtonsProps {
  bewerber: Bewerber;
  onActionComplete?: () => void;
}

export function StatusActionButtons({ bewerber, onActionComplete }: StatusActionButtonsProps) {
  const {
    primaryActions,
    isLoading,
    loadingAction,
    handleAction,
    modals,
    einladenModalProps,
  } = useBewerberActions({
    bewerber,
    onActionComplete,
    includeSecondaryActions: false, // Detail page has separate edit/pflegen buttons
    includeDangerActions: false, // Detail page has separate absage button
  });

  // Also include dynamic actions (mark-reviewed) for display
  const { dangerActions } = useBewerberActions({
    bewerber,
    onActionComplete,
    includeSecondaryActions: false,
    includeDangerActions: true,
  });

  // Only show mark-reviewed action (not absage, which is in separate button)
  const markReviewedAction = dangerActions.find(a => a.key === 'mark-reviewed');
  const allActions = markReviewedAction
    ? [...primaryActions, markReviewedAction]
    : primaryActions;

  if (allActions.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-4 flex items-center flex-wrap gap-2">
        {allActions.map((actionConfig) => {
          const IconComponent = actionConfig.icon;
          return (
            <button
              key={actionConfig.key}
              onClick={() => handleAction(actionConfig)}
              disabled={isLoading || actionConfig.disabled}
              title={actionConfig.tooltip}
              className={`
                inline-flex items-center px-3 py-1.5 border rounded-md text-sm font-medium
                transition-colors duration-150 ease-in-out
                disabled:opacity-50 disabled:cursor-not-allowed
                ${actionConfig.color} ${actionConfig.hoverColor}
              `}
            >
              <IconComponent />
              <span className="ml-1.5">
                {loadingAction === actionConfig.key ? actionConfig.loadingLabel : actionConfig.label}
              </span>
            </button>
          );
        })}
      </div>

      <BewerberActionModals
        bewerber={bewerber}
        modals={modals}
        einladenModalProps={einladenModalProps}
        onModalComplete={onActionComplete}
      />
    </>
  );
}
